import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Plus,
  DollarSign,
  Loader2,
  Sparkles,
} from "lucide-react";

const EXAMPLES = [
  "Surfboard & paddleboard rentals",
  "Beach setup with chairs & umbrella",
  "Birthday or celebration decor",
  "Fridge stocking with premium items",
  "Local guided tours or experiences",
  "Pet sitting or dog walking",
  "Firewood & s'mores kit delivery",
  "Baby gear rental (crib, high chair)",
  "Kayak or boat rental",
  "Photography session",
];

export default function CreateServiceModal({ open, onClose, onSubmit, submitting }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setName("");
    setDescription("");
    setPrice("");
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Service name is required";
    if (name.trim().length > 80) e.name = "Keep it under 80 characters";
    if (price && (isNaN(Number(price)) || Number(price) < 0)) e.price = "Enter a valid price";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const serviceData = {
      key: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim(),
      description: description.trim(),
      category: "Custom",
      pricingModel: "flat",
      pricing: {
        mode: "flat",
        flatUSD: price ? Number(price) : null,
      },
      enabled: true,
      visibility: "public",
      isCustom: true,
    };
    await onSubmit(serviceData);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div
            className="fixed inset-0 bg-black/50"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <div className="min-h-full flex items-center justify-center p-4 py-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col"
            >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                    <Plus size={20} className="text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Create a Service</h2>
                    <p className="text-sm text-gray-500">
                      Offer something unique to your guests
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="px-6 pb-2 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Surfboard Rental"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl outline-none transition-colors ${
                    errors.name
                      ? "border-red-300 focus:border-red-400"
                      : "border-gray-200 focus:border-gray-400"
                  }`}
                  maxLength={80}
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What will guests get? How does it work?"
                  rows={3}
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-gray-400 transition-colors resize-none"
                  maxLength={300}
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guest price (USD) <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex items-center border border-gray-200 rounded-xl px-3.5 focus-within:border-gray-400 transition-colors">
                  <DollarSign size={14} className="text-gray-400 mr-1" />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d.]/g, "");
                      setPrice(v);
                    }}
                    placeholder="0.00"
                    className="flex-1 py-2.5 text-sm outline-none"
                  />
                </div>
                {errors.price && (
                  <p className="text-xs text-red-500 mt-1">{errors.price}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Leave blank to set pricing later. Guests will see this at checkout.
                </p>
              </div>

              {/* Inspiration */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={12} className="text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Ideas from other hosts
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {EXAMPLES.slice(0, 6).map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => {
                        if (!name) setName(ex);
                      }}
                      className="px-2.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 mt-2 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !name.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus size={14} />
                )}
                Create Service
              </button>
            </div>
          </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
